import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/terms.scss";

function Terms() {
  const contact = siteConfig.contact || {};
  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="terms-page">
      <header className="terms-page__hero">
        <p>Legal</p>
        <h1>Terms and conditions</h1>
        <div>
          <p>
            These terms govern your use of this website and purchases made from
            {` ${siteConfig.brandName}`}. Please read them before placing an order.
          </p>
          <p className="terms-page__date">
            Effective <time dateTime="2026-10-19">October 19, 2026</time>
          </p>
        </div>
      </header>

      <div className="terms-page__layout">
        <aside>
          <nav aria-label="Terms and conditions sections">
            <p>Contents</p>
            <a href="#terms-agreement">Agreement</a>
            <a href="#terms-eligibility">Eligibility and accounts</a>
            <a href="#terms-products">Products</a>
            <a href="#terms-orders">Orders</a>
            <a href="#terms-payment">Pricing and payment</a>
            <a href="#terms-fulfillment">Delivery and returns</a>
            <a href="#terms-conduct">Acceptable use</a>
            <a href="#terms-intellectual-property">Intellectual property</a>
            <a href="#terms-liability">Liability</a>
            <a href="#terms-communications">Electronic communications</a>
            <a href="#terms-general">General provisions</a>
            <a href="#terms-contact">Changes and contact</a>
          </nav>
        </aside>

        <article className="terms-page__content">
          <section id="terms-agreement" aria-labelledby="terms-agreement-title">
            <span>01</span>
            <h2 id="terms-agreement-title">Agreement to these terms</h2>
            <p>
              By accessing this website or submitting an order, you agree to these
              terms and our <Link to="/privacy" onClick={toTop}>Privacy Policy</Link>.
              If you do not agree, do not use the website. The terms in effect when
              an order is accepted form part of the contract for that purchase.
            </p>
            <p>
              Mandatory consumer protections in your place of residence continue
              to apply and are not limited by these terms.
            </p>
          </section>

          <section id="terms-eligibility" aria-labelledby="terms-eligibility-title">
            <span>02</span>
            <h2 id="terms-eligibility-title">Eligibility and accounts</h2>
            <p>
              You must have legal capacity to enter a purchase contract and provide
              accurate, current information at checkout. Purchases for resale or
              other commercial purposes are not permitted without written approval.
            </p>
            <p>
              You are responsible for activity under your account and for keeping
              sign-in credentials confidential. Tell Client Services promptly if
              you believe an account has been accessed without permission.
            </p>
          </section>

          <section id="terms-products" aria-labelledby="terms-products-title">
            <span>03</span>
            <h2 id="terms-products-title">Product information</h2>
            <p>
              We aim to present descriptions, measurements, composition, and color
              accurately. Screen settings and the individual character of natural
              materials can result in minor variations. Such variations are not
              necessarily defects.
            </p>
            <p>
              Availability is not guaranteed. We may correct an error, update
              information, or withdraw a product before an order is accepted.
              Product care instructions supplied with an item should be followed.
            </p>
          </section>

          <section id="terms-orders" aria-labelledby="terms-orders-title">
            <span>04</span>
            <h2 id="terms-orders-title">Orders and acceptance</h2>
            <p>
              Adding an item to the cart does not reserve it. Your order is an offer
              to purchase, and the automated acknowledgement confirms receipt only.
              A contract is formed when we send a shipping confirmation, unless
              local law provides otherwise.
            </p>
            <p>We may decline or cancel an order where:</p>
            <ul>
              <li>The product is unavailable or contains a clear pricing or description error.</li>
              <li>Payment cannot be authorized or fraud screening identifies material risk.</li>
              <li>Delivery information is incomplete, invalid, or outside the selected market.</li>
              <li>Quantity limits or restrictions on commercial resale have been exceeded.</li>
            </ul>
            <p>If payment has already been captured for a canceled order, it will be refunded.</p>
          </section>

          <section id="terms-payment" aria-labelledby="terms-payment-title">
            <span>05</span>
            <h2 id="terms-payment-title">Pricing and payment</h2>
            <p>
              Prices are displayed in the currency selected for your market. Taxes,
              delivery fees, duties, and other charges are shown where applicable
              before order submission. Your payment method may be authorized when
              the order is placed and charged in accordance with the checkout notice.
            </p>
            <p>
              Promotions are subject to their stated conditions, validity dates,
              product exclusions, and market restrictions. Unless expressly stated,
              offers cannot be combined or applied after purchase.
            </p>
          </section>

          <section id="terms-fulfillment" aria-labelledby="terms-fulfillment-title">
            <span>06</span>
            <h2 id="terms-fulfillment-title">Delivery, title, and returns</h2>
            <p>
              Estimated delivery dates are not guaranteed and may be affected by
              carrier or customs events outside our reasonable control. Risk passes
              as provided by applicable consumer law and the selected delivery terms.
            </p>
            <div className="terms-page__related">
              <Link to="/shipping" onClick={toTop}>
                Shipping and delivery
              </Link>
              <Link to="/returns" onClick={toTop}>
                Returns and refunds
              </Link>
            </div>
          </section>

          <section id="terms-conduct" aria-labelledby="terms-conduct-title">
            <span>07</span>
            <h2 id="terms-conduct-title">Acceptable use</h2>
            <p>You may use the site only for lawful, personal purposes. You must not:</p>
            <ul>
              <li>Interfere with security, availability, or operation of the website.</li>
              <li>Use automated tools to extract content, inventory, prices, or customer data without permission.</li>
              <li>Introduce malicious code, impersonate another person, or submit false information.</li>
              <li>Use site content or services in a way that infringes the rights of others.</li>
            </ul>
            <p>We may suspend access where reasonably necessary to protect customers, the website, or our rights.</p>
          </section>

          <section id="terms-intellectual-property" aria-labelledby="terms-intellectual-property-title">
            <span>08</span>
            <h2 id="terms-intellectual-property-title">Intellectual property</h2>
            <p>
              The website and its text, photography, graphics, product names,
              layout, software, and other materials are owned by or licensed to
              {` ${siteConfig.brandName}`} and protected by applicable law. A limited,
              revocable right is granted to browse and shop for personal use.
            </p>
            <p>
              No content may be reproduced, modified, distributed, sold, or used
              commercially without prior written permission, except where the law
              expressly permits it.
            </p>
          </section>

          <section id="terms-liability" aria-labelledby="terms-liability-title">
            <span>09</span>
            <h2 id="terms-liability-title">Warranties and liability</h2>
            <p>
              Nothing in these terms excludes liability that cannot lawfully be
              excluded, including applicable rights relating to defective goods.
              Subject to those rights, we are not responsible for indirect or
              unforeseeable loss, business loss arising from personal-use purchases,
              or interruption caused by events beyond reasonable control.
            </p>
            <p>
              The website is provided with reasonable care, but uninterrupted or
              error-free access cannot be guaranteed. Links to independent websites
              are provided for convenience and do not make us responsible for their
              content or practices.
            </p>
          </section>

          <section id="terms-communications" aria-labelledby="terms-communications-title">
            <span>10</span>
            <h2 id="terms-communications-title">Electronic communications</h2>
            <p>
              Order acknowledgements, receipts, delivery updates, return messages,
              security notices, and customer-service responses may be provided
              electronically to the contact details you submit. Keep those details
              accurate and retain relevant messages for your records.
            </p>
            <p>
              Marketing communications are optional and can be withdrawn separately.
              More information is available in our <Link to="/electronic-communications" onClick={toTop}>Electronic Communications Policy</Link>.
            </p>
          </section>

          <section id="terms-general" aria-labelledby="terms-general-title">
            <span>11</span>
            <h2 id="terms-general-title">General provisions</h2>
            <p>
              We are not responsible for delay caused by events beyond reasonable
              control, but this does not remove rights that cannot lawfully be
              excluded. If part of these terms is unenforceable, the remaining parts
              continue to apply to the extent permitted by law.
            </p>
            <p>
              A delay in enforcing a right is not a waiver. You may not transfer an
              order or rights under it for commercial resale without consent. We may
              transfer our rights and duties as part of a legitimate business change
              if doing so does not reduce mandatory consumer protections.
            </p>
          </section>

          <section id="terms-contact" aria-labelledby="terms-contact-title">
            <span>12</span>
            <h2 id="terms-contact-title">Governing terms, changes, and contact</h2>
            <p>
              Applicable law and courts are determined by the selling entity and
              your place of residence, without depriving you of mandatory local
              consumer protections. We may revise website terms prospectively; the
              effective date above identifies the current version.
            </p>
            <div className="terms-page__contact">
              <p>Questions about these terms</p>
              {contact.email && <a href={`mailto:${contact.email}`}>{contact.email}</a>}
              {contact.phone && <a href={`tel:${contact.phone}`}>{contact.phone}</a>}
              <Link to="/contact" onClick={toTop}>
                Contact Client Services
              </Link>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Terms;
